import React, { Component } from 'react';
import PropTypes from 'prop-types';
import pluralize from 'pluralize';
import classNames from 'classnames';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';

import { isUserAgentIphoneApp } from 'common/util/isUserAgentMobileApp';
import getTourListingsGoogleMapsDirectionsUrl from 'common/util/getTourListingsGoogleMapsDirectionsUrl';

import {
  LISTING_DATA_SOURCE_SMART_APARTMENT,
  LISTING_PHOTO_DIMENSION_THUMBNAIL,
} from 'common/listingConstants';

import {
  TOUR_STATUS_REQUESTED,
  TOUR_STATUS_SCHEDULED,
  TOUR_STATUS_COMPLETED,
  TOUR_STATUS_CANCELLED,
  TOUR_SHOWING_STATUS_PENDING,
  TOUR_SHOWING_STATUS_CONFIRMED,
  TOUR_SHOWING_STATUS_FAILED,
  TOUR_SHOWING_STATUS_UNREAD,
  TOUR_SHOWING_STATUS_COMPLETED,
} from 'common/tourConstants';
import {
  getListingFieldsForDisplay,
  getListingUrlPath,
  getListingManagedPhotoUrls,
} from 'common/listingHelpers';

import { numberWithCommas, bedBathsDisplayString } from 'common/formatters';

import { getTourShowingLabel, isLockedTour } from 'common/tourHelpers';

import log from 'client/lib/log';
import SrApplicationButton from 'client/components/SrApplicationButton';
import SrRequestShowingButton from 'client/components/SrRequestShowingButton';
import SrTourListRowEditButton from 'client/components/SrTourListRowEditButton';
import SrExternalLink from 'client/components/SrExternalLink';
import SrLink from 'client/components/SrLink/SrLink';

function getNumberSuffix(number) {
  const numberArray = `${number}`.split('').reverse();

  if (numberArray[1] !== '1') {
    switch (numberArray[0]) {
      case '1':
        return 'st';
      case '2':
        return 'nd';
      case '3':
        return 'rd';
    }
  }

  return 'th';
}

function getTourShowingClassName({ showingStatus, isUpcomingTour, isRecommendation }) {
  if (isRecommendation) {
    return 'nearbyMatch';
  }

  return showingStatus === TOUR_SHOWING_STATUS_FAILED && isUpcomingTour
    ? 'notAvailable'
    : showingStatus;
}

function getShowingStatusLabel({
  showingStatus,
  tourStatus,
  tourListingNote,
  isRecommendation,
  confirmationOrderDisplay,
  showingStatusLabel,
}) {
  if (isRecommendation) {
    return 'Nearby Match';
  }

  if (
    showingStatus === TOUR_SHOWING_STATUS_PENDING ||
    showingStatus === TOUR_SHOWING_STATUS_UNREAD
  ) {
    return showingStatusLabel;
  }
  if (showingStatus === TOUR_SHOWING_STATUS_FAILED && tourListingNote) {
    return showingStatusLabel + ': ' + tourListingNote;
  }
  if (showingStatus === TOUR_SHOWING_STATUS_CONFIRMED && tourStatus === TOUR_STATUS_SCHEDULED) {
    return showingStatusLabel + ' ' + confirmationOrderDisplay;
  }
  if (showingStatus === TOUR_SHOWING_STATUS_FAILED) {
    return showingStatusLabel;
  }
  if (showingStatus === TOUR_SHOWING_STATUS_CONFIRMED) {
    return showingStatusLabel;
  }
  if (showingStatus === TOUR_SHOWING_STATUS_COMPLETED) {
    return showingStatusLabel;
  }

  log.error(new Error(`Unhandled showing label for showing status "${showingStatus}"`));
  return null;
}

class SrTourListRow extends Component {
  static propTypes = {
    listing: PropTypes.object.isRequired,
    tour: PropTypes.object.isRequired,
    onEdit: PropTypes.func,
    onConfirmRemoveLastListing: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    listingNumberToSee: PropTypes.number,
    highlight: PropTypes.bool,
    isRecommendation: PropTypes.bool,
    requestedFrom: PropTypes.string,
    listingPhotoResolution: PropTypes.number,
    userAgent: PropTypes.object.isRequired,
  };

  render() {
    const {
      requestedFrom,
      isRecommendation,
      listing,
      tour,
      onEdit,
      onConfirmRemoveLastListing,
      listingNumberToSee,
      onClose,
      highlight,
      listingPhotoResolution,
      userAgent,
    } = this.props;

    const listingFieldsForDisplay = getListingFieldsForDisplay({ listing });

    const onClickEdit = (e) => {
      e.stopPropagation();
      e.preventDefault();

      isLockedTour({
        tour: tour,
        onUnlocked: () => {
          onEdit({
            listing: listing,
            tourId: tour.id,
            listingDisplayText: listing.addressStreetDisplay,
          });
        },
      });
    };

    const onConfirmSaveLastListing = (e) => {
      e.stopPropagation();
      e.preventDefault();

      isLockedTour({
        tour: tour,
        onUnlocked: () => {
          onConfirmRemoveLastListing({
            listing: listing,
            tourId: tour.id,
          });
        },
      });
    };

    const listingGoogleMapsDirectionsUrl = getTourListingsGoogleMapsDirectionsUrl({
      listings: [listing],
      showings: [
        {
          listingId: listing.id,
          status: listing.showingStatus,
        },
      ],
      isIphoneApp: isUserAgentIphoneApp(userAgent),
    });

    const isUpcomingTour = [
      TOUR_SHOWING_STATUS_UNREAD,
      TOUR_STATUS_REQUESTED,
      TOUR_STATUS_SCHEDULED,
    ].includes(tour.status);

    const showingStatus = listing.tourListingStatus;

    const managedPhotoUrls = getListingManagedPhotoUrls({
      listing: listing,
      dimension: LISTING_PHOTO_DIMENSION_THUMBNAIL,
      resolution: listingPhotoResolution,
    });

    const tourShowingClassName = getTourShowingClassName({
      showingStatus,
      isRecommendation,
      isUpcomingTour,
    });

    const tourStatusLabel =
      tour.status === TOUR_STATUS_CANCELLED
        ? 'Archived'
        : getTourShowingLabel({
            tourShowingStatus: showingStatus,
            isCompletedTour: tour.status === TOUR_STATUS_COMPLETED,
            hasNote: listing.tourListingNote,
          });

    const confirmationOrderDisplay = ` to see ${listingNumberToSee}${getNumberSuffix(
      listingNumberToSee,
    )}`;

    const currentTime = momentWithAustinTimezone();
    const tourStartTime = momentWithAustinTimezone(tour.startDate);
    let isTourLocked;
    if (
      tour.status === TOUR_STATUS_SCHEDULED &&
      tour.markedNeedsReschedulingFlag === false &&
      !currentTime.isBefore(tourStartTime.subtract(60, 'minutes'))
    ) {
      isTourLocked = true;
    } else {
      isTourLocked = false;
    }

    const showingStatusDisplayLabel = getShowingStatusLabel({
      showingStatus,
      tourStatus: tour.status,
      tourListingNote: listing.tourListingNote,
      isRecommendation,
      confirmationOrderDisplay,
      showingStatusLabel: tourStatusLabel,
    });

    return (
      <div className="container SrTourListRow">
        <SrLink
          to={getListingUrlPath(listing)}
          className={classNames('tourRow clearfix', tourShowingClassName, { highlight })}
        >
          <img className="thumbnail" src={managedPhotoUrls[0]} alt={listing.addressStreetDisplay} />
          <div className="descriptionContainer">
            <div className="address">
              <span>{listing.addressStreetDisplay}</span>
            </div>

            <div className="details">
              <span className="price">${numberWithCommas(listingFieldsForDisplay.price)}</span>
              <span className="bedbath">
                {listing.dataSource === LISTING_DATA_SOURCE_SMART_APARTMENT
                  ? `${pluralize('unit', listing.apartmentUnits.length, true)}`
                  : bedBathsDisplayString(listingFieldsForDisplay)}
              </span>
            </div>

            <div className={`status ${tourShowingClassName}`}>
              <span className="status-failed-label">{showingStatusDisplayLabel}</span>
            </div>
          </div>

          {isRecommendation && (
            <div className="rightAction">
              <SrRequestShowingButton
                requestedFrom={requestedFrom}
                listing={listing}
                isRecommendation
              />
            </div>
          )}

          {!isRecommendation && (
            <div className="rightAction">
              {tour.status === TOUR_STATUS_COMPLETED ? (
                <div className={listing.hasOnlineApp ? 'isOnlineApp' : null}>
                  <SrApplicationButton listing={listing} onClickEdit={onClickEdit} />
                </div>
              ) : tour.status === TOUR_STATUS_CANCELLED ||
                (tour.status === TOUR_STATUS_SCHEDULED &&
                  listing.tourListingStatus === TOUR_SHOWING_STATUS_FAILED) ? null : tour.status ===
                  TOUR_STATUS_SCHEDULED && isTourLocked ? (
                <SrExternalLink
                  className="button grey directions"
                  link={listingGoogleMapsDirectionsUrl}
                >
                  get directions
                </SrExternalLink>
              ) : (
                <SrTourListRowEditButton
                  tour={tour}
                  listing={listing}
                  onClickEdit={onClickEdit}
                  onConfirmSaveLastListing={onConfirmSaveLastListing}
                  onClose={onClose}
                />
              )}
            </div>
          )}
        </SrLink>
      </div>
    );
  }
}

SrTourListRow.displayName = 'SrTourListRow';

export default SrTourListRow;
